import React, { useCallback, useMemo, useState } from 'react';
import Inspector, { Band, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { useRunAction } from '../../components/nucleus/actions';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { kindOne } from '../nucleus/RealmsModel';
import AccessList from '../../components/nucleus/AccessList';
import { RealmChooser, RealmLocked, Section } from '../../components/grant/parts';
import FaceChoice from '../../components/grant/FaceChoice';
import { ConsentFrame, ViewTabs, TermsRow, PreviewPane } from '../../components/nucleus/GrantViews';
import { GrantMark } from '../nucleus/RealmsParts';
import { HistoryModal } from '../realms/RealmsModals';
import { statusOf, statusText, STATUS_WORD, lapsingSoon, consentHref, homeOf, replacedBy } from './accessData';
import { isStanding, standingDoor, standingLive } from './standing';
import { until, dateOf, ago } from '../../nucleus/format';

const adminHintOf = (spaceName) => (what) => `Only an admin of ${spaceName || 'this group'} can ${what} its contracts`;
const CANT_OPEN = 'This one can’t be changed from here';
const go = (href) => { window.location.href = href; };

// One grant's acts, the same in the inspector and on its cell's right-click.
// Reading (its history) is a link in a band, not an act.
export function grantActionsOf(p, { isAdmin, spaceName, calls, toast, reload, hrefs, openMember, openHref, to, newToken }) {
    const adminHint = adminHintOf(spaceName);
    if (p.kind === 'token') return tokenActionsOf(p, { isAdmin, adminHint, calls, toast, reload, newToken });
    // a group's side of a membership changes with the membership, in People: change
    // realm, demote, remove; never paused, revoked or reshaped as a contract
    if (p.entityType === 'group_member') {
        return openMember ? [{ id: 'member', label: 'Open member', primary: true, hint: `Change their realm, or what they do in ${spaceName || 'the group'}, in People`, run: () => openMember(p) }] : [];
    }
    if (isStanding(p)) return standingActionsOf(p, { openHref, to });
    const status = statusOf(p);
    const name = p.label;
    const edit = hrefs.edit(p);
    const renew = hrefs.renew(p);
    const acts = [];
    // a friend request's offer: nothing to resume or change until they answer
    if (status === 'waiting') {
        return [{
            id: 'cancel-request', label: 'Cancel request', disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('cancel'),
            confirm: { title: `Cancel your friend request to ${name}?`, body: 'What you offered is withdrawn, and nothing was ever shared. You can ask again.', label: 'Cancel request' },
            run: async () => { await calls.cancelRequest(p); toast.success('Request cancelled'); reload(); },
        }];
    }
    if (status === 'paused') {
        acts.push({
            id: 'resume', label: 'Resume', primary: true, disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('resume'),
            run: async () => { await calls.resume(p); toast.success(`${name} gets its access again.`); reload(); },
        });
    }
    if (status === 'active' || status === 'paused') {
        acts.push({
            id: 'edit', label: 'Change', primary: status === 'active',
            disabled: !isAdmin || !edit, hint: !isAdmin ? adminHint('change') : edit ? 'Change what it gets, where it was granted' : CANT_OPEN,
            run: () => go(edit),
        });
    }
    if (status === 'expired' || lapsingSoon(p)) {
        acts.push({
            id: 'renew', label: 'Extend', primary: status === 'expired',
            disabled: !isAdmin || !renew, hint: !isAdmin ? adminHint('extend') : renew ? 'Choose a new expiry, where it was granted' : CANT_OPEN,
            run: () => go(renew),
        });
    }
    if (status === 'revoked') {
        const again = hrefs.again(p);
        acts.push({
            id: 'again', label: 'Grant again', primary: true,
            disabled: !isAdmin || !again, hint: !isAdmin ? adminHint('make') : again ? 'A new contract, made where it was granted' : CANT_OPEN,
            run: () => go(again),
        });
    }
    if (status === 'active') {
        acts.push({
            id: 'pause', label: 'Pause', disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('pause'),
            run: async () => { await calls.pause(p); toast.success(`${name} is paused. It gets nothing until you resume it.`); reload(); },
        });
    }
    if (status === 'active' || status === 'paused') {
        acts.push({
            id: 'disconnect', label: 'Revoke', danger: true, disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('revoke'),
            confirm: {
                title: `Revoke ${name}’s contract?`,
                body: `${name} stops getting what ${p.realms.map((r) => r.name).join(' + ') || p.realmName} cover${p.realms.length === 1 ? 's' : ''} on its next request. Whatever it already copied stays copied, and granting again later starts a new contract.`,
                label: 'Revoke',
            },
            run: async () => { await calls.disconnect(p); toast.success(`${name}’s contract is revoked.`); reload(); },
        });
    }
    return acts;
}

// AN ACCESS TOKEN'S CONTRACT. Paused and resumed like any; revoked as a token, so the
// token and its contract end together. What it reaches and when it lapses are what
// it was made with: changing either is making a new one, so Change and Extend stand
// greyed and say so, and an ended one is made again from its own sheet.
const FIXED = 'A token keeps what it was made with. Make a new one to change it.';
function tokenActionsOf(p, { isAdmin, adminHint, calls, toast, reload, newToken }) {
    const status = statusOf(p);
    const name = p.label;
    const acts = [];
    if (!p.live) {
        acts.push({
            id: 'again', label: 'Make again', primary: true,
            disabled: !isAdmin || !newToken, hint: isAdmin ? 'A new token, on the same realms' : adminHint('make'),
            run: () => newToken({ realmIds: p.realmIds.length ? p.realmIds : [p.realmId].filter(Boolean), name }),
        });
        return acts;
    }
    if (status === 'paused') {
        acts.push({
            id: 'resume', label: 'Resume', primary: true, disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('resume'),
            run: async () => { await calls.resume(p); toast.success(`${name} works again.`); reload(); },
        });
    }
    acts.push({ id: 'edit', label: 'Change', disabled: true, hint: FIXED, run: () => {} });
    if (lapsingSoon(p)) acts.push({ id: 'renew', label: 'Extend', disabled: true, hint: FIXED, run: () => {} });
    if (status === 'active') {
        acts.push({
            id: 'pause', label: 'Pause', disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('pause'),
            run: async () => { await calls.pause(p); toast.success(`${name} is paused. Anything using it is refused until you resume it.`); reload(); },
        });
    }
    acts.push({
        id: 'disconnect', label: 'Revoke', danger: true, disabled: !isAdmin, hint: isAdmin ? undefined : adminHint('revoke'),
        confirm: {
            title: `Revoke ${name}?`,
            body: `Anything using ${name} is refused from its next request. Whatever it already read stays read. A revoked token is never used again.`,
            label: 'Revoke',
        },
        run: async () => { await calls.revokeToken(p); toast.success(`${name} is revoked.`); reload(); },
    });
    return acts;
}

// A schedule's or an automation's contract: its realm is chosen with the schedule
// (or the automation), and the contract is made again from it, so it is changed
// there. Here, the door to it; changing or revoking it here would leave the schedule
// blocked, so those acts say where to go instead.
function standingActionsOf(p, { openHref, to }) {
    const s = p.standing || {};
    const door = to ? standingDoor(s.automation, p.entityId, to) : null;
    const acts = [];
    // the bar's cells are narrow: the door is named by what it opens
    if (door && openHref) acts.push({ id: 'open-link', label: door.label, primary: true, run: () => openHref(door.href) });
    if (!p.live) return acts;
    const where = s.schedule ? 'on its tool, in Plugins' : 'with the automation, in Automations';
    acts.push({ id: 'edit', label: 'Change', disabled: true, hint: `Its realm is chosen ${where}`, run: () => {} });
    acts.push({
        id: 'disconnect', label: 'Revoke', danger: true, disabled: true, run: () => {},
        hint: s.schedule ? 'Turn off Runs automatically on its tool, in Plugins, instead: without this contract it can’t run' : 'Turn the automation off in Automations instead: without this contract it can’t run',
    });
    return acts;
}

// The hrefs a grant's acts go to: the consent page, coming back here, open on it.
export function useGrantHrefs() {
    const { groupId, page, to } = usePlace();
    return useMemo(() => {
        const back = (p) => `${window.location.origin}${to('realms', page?.id, `open=${encodeURIComponent(p.key)}`)}`;
        return {
            edit: (p) => consentHref(p, { groupId, back: back(p) }),
            renew: (p) => consentHref(p, { groupId, back: back(p), edit: p.entityType !== 'app' }),
            again: (p) => consentHref(p, { groupId, back: back(p), edit: p.entityType !== 'app' }),
        };
    }, [groupId, page, to]);
}

const ABOUT = {
    app: (p, space) => `An app connected to ${space}.`,
    token: (p, space, inGroup) => `A token${p.token?.madeBy && inGroup ? ` ${p.token.madeBy} made` : ''} for a script or tool to use ${inGroup ? `${space}’s` : 'your'} realm. It keeps the realm and what it may do that it was made with.`,
    delegate: (p) => `An agent inside its app, under the key ${p.subKey}. It gets at most what its app gets.`,
    friend: () => 'A friend you share with.',
    user: (p, space, inGroup) => (inGroup ? `Someone ${space} shares with.` : 'Someone you share with.'),
    group_member: (p, space) => `${space}’s side of ${p.label}’s membership: what they can do in it. Their side, what they share with ${space}, is their own contract: ${space} reads it, it can’t change it.`,
    group: () => 'A group you belong to: what you share into it.',
    cosm_agent: () => 'A COSM agent: its own account, given this realm in COSM. It reads, and never writes. Change what it gets in COSM; pause or revoke it here.',
    group_connection: () => 'A group this one is connected to.',
    public: (p, space, inGroup) => (inGroup ? `${space}’s public page: what anyone may see.` : 'Your public page: what anyone may see.'),
    plugin_execution: () => 'One run of a plugin: what that run could reach.',
    plugin_delegation: () => 'A plugin run on an app’s behalf: what that run could reach.',
};

// HOW YOU APPEAR, as the consent page's FaceChoice reads it: the identities this
// grant may present (yours, each with its photo; your default marked), which one it
// shows, and where that answer came from.
function faceProps(p, data) {
    const identities = data.faces.map((f) => ({
        id: f.id, name: f.name, avatar_url: f.node?.avatar_url || null, is_default: f.id === String(data.defaultFaceId || ''),
    }));
    const sources = Object.fromEntries(p.realms.filter((r) => r.profileNodeId).map((r) => [String(r.profileNodeId), r.name]));
    const faceId = p.faceId ? String(p.faceId) : (data.defaultFaceId ? String(data.defaultFaceId) : null);
    return {
        identities,
        sources,
        face: {
            id: faceId,
            showsAll: p.showsAll,
            source: p.faceSource === 'chosen' ? 'chosen' : p.faceSource === 'realm' ? 'realm' : null,
            realmName: faceId ? sources[faceId] : null,
        },
    };
}

// What "as its realms say" would show: the identity its realms agree on, else your default.
function followOf(sources, data) {
    const proposed = Object.keys(sources || {});
    if (proposed.length === 1) {
        const name = data.faces.find((f) => String(f.id) === proposed[0])?.name;
        return { id: proposed[0], note: `${name || 'its identity'}, from ${sources[proposed[0]]}` };
    }
    if (proposed.length > 1) return { id: null, note: 'its realms disagree: you will be asked' };
    const d = data.defaultFaceId ? String(data.defaultFaceId) : null;
    return { id: d, note: d ? `${data.faces.find((f) => String(f.id) === d)?.name || 'your default'}, your default` : 'your default' };
}

// ONE EXPOSURE PROFILE, read as the consent page that made it. Two tabs:
// PERMISSION RULES, in the consent page's order (the privacy realms it is shared
// from, how you appear, what it can do, the terms), and PREVIEW, the nodes it can
// actually see right now. Every act on it is in the one ActionBar.
export default function GrantInspector({ connection, grantId, data, calls, actionsFor, onClose }) {
    const { isAdmin, groupId, spaceName, to, navigate } = usePlace();
    const inGroup = !!groupId;
    const run = useRunAction();
    const toast = useToast();
    const [pickedId, setPickedId] = useState(grantId || connection.first.id);
    const [view, setView] = useState('rules');
    const [reading, setReading] = useState(false);
    const p = connection.all.find((x) => x.id === pickedId) || connection.first;
    const status = statusOf(p);
    // a member's contract is one realm, changed in People (Change realm), never here
    const member = p.entityType === 'group_member';
    // a schedule's or an automation's: what it runs with, changed with the schedule
    const standing = isStanding(p) ? (p.standing || {}) : null;
    // a COSM agent's grant is one realm, given and changed in COSM (AGENT_GRANT_FIXED);
    // here it is paused, resumed or revoked like any other
    const cosmAgent = p.entityType === 'cosm_agent';
    const editable = isAdmin && p.live && !member && !standing && !cosmAgent;
    // a token keeps the realms it was made with: they are read, never added to
    const token = p.kind === 'token' ? p.token || {} : null;
    const earlier = connection.all.filter((x) => x.id !== p.id);
    const home = standing ? standingDoor(standing.automation, p.entityId, to) : homeOf(p, to);
    // an app's older live contract: its requests use the newest
    const replaced = replacedBy(p, connection);
    const usedLine = (x) => x.realms.map((r) => r.name).join(' + ') || x.realmName;
    const expiring = lapsingSoon(p);
    const realmsLine = p.realms.map((r) => r.name).join(' + ') || p.realmName;
    const loadPreview = useCallback((q) => calls.preview(p, q), [calls, p]);
    // AN APP'S OR AGENT'S TOOLS: given on the contract itself, whatever its realms
    // cover; each a door to its plugin's Tool access sheet
    const isApp = p.entityType === 'app';
    const access = isApp && p.consent?.plugins ? { ...p.blob, plugins: p.consent.plugins } : p.blob;
    const toolDoor = isApp && p.live ? (pluginId) => navigate(to('plugins', null, `open=${encodeURIComponent(pluginId)}&access=${encodeURIComponent(p.id)}`)) : null;
    // every realm it may be shared from: never another recipient's just-for-one realm,
    // which holds what was shared with them alone (the backend refuses it too), and
    // never a group's No access or Members, which are for its members (Full Access,
    // its default, is offered as ever)
    const offered = data.realms.filter((r) => (!r.builtin || r.builtin === 'full_access' || p.realmIds.includes(r.id)) && (!r.shared || p.realmIds.includes(r.id)
        || (r.recipientId === String(p.entityId) && (!r.recipientType || r.recipientType === p.entityType))));

    // the privacy realms it is shared from, as the consent page lists them:
    // pressing one adds it, pressing an added one removes it (never the last)
    const change = (label, fn, done) => run({ id: label, label, run: async () => { await fn(); if (done) toast.success(done); data.reloadGrants(); } });
    const toggleRealm = (realmId) => {
        const realm = data.realms.find((r) => r.id === String(realmId));
        if (!realm) return;
        if (!p.realmIds.includes(realm.id)) {
            change('Add', () => calls.attach(p, realm.id), `${p.label} also gets what ${realm.name} covers.`);
            return;
        }
        if (p.realms.length < 2) {
            toast.error(`${realm.name} is its only realm. Revoke the contract to stop sharing.`);
            return;
        }
        run({
            id: 'detach', label: 'Remove', danger: true,
            confirm: {
                title: `Stop sharing ${realm.name} with ${p.label}?`,
                body: `${p.label} stops getting what ${realm.name} covers. It still gets what ${p.realms.filter((r) => r.id !== realm.id).map((r) => r.name).join(' + ')} cover.`,
                label: 'Stop sharing',
            },
            run: async () => { await calls.detach(p, realm); toast.success(`${realm.name} is no longer shared with ${p.label}.`); data.reloadGrants(); },
        });
    };

    const faces = faceProps(p, data);
    const setFace = (body) => change('How you appear', () => calls.setFace(p, body), inGroup ? `${p.label} sees ${spaceName} as chosen.` : `${p.label} sees you as you chose.`);
    const pickFace = (id) => setFace({ ...(p.showsAll ? { shows_all_identities: false } : {}), profile_node_id: id || null });

    const kindWord = standing ? (standing.schedule ? 'Runs automatically' : 'Automation') : kindOne(p.kind);
    const stateWord = standing && p.live ? standing.word : replaced ? 'Not used' : statusText(p);
    const sub = standing && p.live
        ? (standing.schedule ? `The access its tool runs with when it runs automatically, in ${realmsLine}.` : `The access this automation runs with, in ${realmsLine}.`)
        : p.live && member
            ? `What ${p.label} can do in ${spaceName}, from ${realmsLine}.${dateOf(p.created) ? ` Since ${dateOf(p.created)}.` : ''}`
            : p.live
                ? `Shared from ${realmsLine}.${dateOf(p.created) ? ` Granted ${dateOf(p.created)}.` : ''}`
                : `${STATUS_WORD[status]} ${dateOf(p.revoked || p.expires)}. It was shared from ${realmsLine}.`;
    // what it is, said as facts: a schedule's state and why; an older contract's replacement
    const about = standing
        ? [
            standing.word && (standing.when ? `${standing.word} · ${standing.when}.` : `${standing.word}.`),
            standing.why && `${standing.why.replace(/\.$/, '')}.`,
            standing.gone && (standing.schedule ? 'Its tool no longer runs automatically.' : 'Its automation no longer exists.'),
            p.live && !standing.gone && `Its realm is chosen ${standing.schedule ? 'on its tool, in Plugins' : 'with the automation, in Automations'}.`,
        ].filter(Boolean).join(' ') || `${kindWord}.`
        : `${ABOUT[p.kind === 'delegate' || p.kind === 'token' ? p.kind : p.entityType]?.(p, spaceName, inGroup) || `${kindOne(p.kind)}.`}${replaced ? ` Not used: requests from ${p.label} use its newer contract, shared from ${usedLine(replaced)}.` : ''}${member ? ' Every change since is in its history.' : ' Every change since it was granted is in its history.'}`;

    // paused is not dead: the owner's preview of it reads what it would show once
    // resumed (as_if_resumed); anyone else's reads nothing, and says so (`idle`)
    const paused = `Paused, so ${p.label} sees nothing until you resume it.`;
    const dead = status === 'waiting' ? `Waiting, so ${p.label} sees nothing until they accept.`
        : status === 'revoked' ? `Revoked, so ${p.label} sees nothing.`
            : status === 'expired' ? `Expired, so ${p.label} sees nothing until you extend it.` : null;

    return (
        <Inspector
            face={<GrantMark profile={p} size={64} dim={!p.live} />}
            tag={['Contract', kindWord, stateWord].filter(Boolean).join(' · ')}
            title={p.label}
            sub={sub}
            actions={actionsFor(p)}
            onClose={onClose}
        >
            <Band title="About" aside={<span className="nx-band__links">
                {home && <button type="button" className="nx-link" onClick={() => navigate(home.href)}>{home.label}</button>}
                <button type="button" className="nx-link" onClick={() => setReading(true)}>History</button>
            </span>}>
                <Note>{about}</Note>
            </Band>
            <ConsentFrame>
                <ViewTabs value={view} onChange={setView}
                    tabs={earlier.length ? [['rules', 'Permission rules'], ['preview', 'Preview'], ['others', `Other contracts (${earlier.length})`]] : undefined} />
                {view === 'rules' ? (<>

                    <Section title={inGroup ? `How ${spaceName} appears` : 'How you appear'}>
                        <FaceChoice
                            {...faces}
                            owned={faces.identities.length}
                            everyone={faces.identities}
                            appName={p.label}
                            onPick={editable ? pickFace : undefined}
                            onFollow={editable ? () => pickFace(null) : undefined}
                            follow={followOf(faces.sources, data)}
                            onShowAll={editable ? (v) => setFace({ shows_all_identities: v }) : undefined}
                        />
                    </Section>

                    <Section title="Which realms" aside={p.realms.length ? `${p.realms.length} added` : null}>
                        {editable && !token
                            ? <RealmChooser realms={offered} selectedIds={p.realmIds} onSelect={toggleRealm} />
                            : <div className="gp-realms"><div className="gp-realms__scroll">{p.realms.map((r) => <RealmLocked key={r.id} title={r.name} />)}</div></div>}
                    </Section>

                    <Section title={`What ${p.label} can do`}>
                        <AccessList access={access} schemaNames={data.schemaNames} loadTitles={calls.nodeTitles} tools={isApp} toolDoor={toolDoor}
                            empty={p.live ? `Nothing: its realms cover none of what ${inGroup ? 'was' : 'you'} approved.` : 'Nothing now.'} />
                    </Section>

                    <TermsRow items={[
                        p.revoked
                            ? { label: 'Revoked', value: dateOf(p.revoked) }
                            : { label: 'Expires', value: p.expires ? (p.expired ? dateOf(p.expires) : `${until(p.expires, { days: true })}, ${dateOf(p.expires)}`) : 'Never expires', tone: p.expired || expiring ? 'warn' : undefined },
                        standing && p.live
                            ? standing.known && { label: kindWord, value: standing.word, tone: standingLive(standing) ? undefined : 'warn' }
                            : { label: 'Status', value: stateWord, tone: status === 'active' && !replaced ? undefined : 'warn' },
                        token && { label: 'Last used', value: token.lastUsed ? ago(token.lastUsed) : 'Never' },
                        token && inGroup && token.madeBy && { label: 'Made by', value: token.madeBy },
                    ]} />
                    <p className="gp-rcpt__foot nx-consent__foot">
                        <span>Granted</span> <b>{dateOf(p.created) || '—'}</b>
                        {!standing && (<>
                            <span className="gp-rcpt__sep">·</span>
                            <span>Revocable at any time, effective on the next request</span>
                        </>)}
                    </p>
                </>) : view === 'others' && earlier.length ? (
                    <div className="nx-consent__pane">
                        <div className="nx-consent__rows">
                            {earlier.map((x) => {
                                const s = statusOf(x);
                                const line = x.realms.map((r) => r.name).join(' + ') || x.realmName;
                                return (
                                    <Row
                                        key={x.id}
                                        lead={<GrantMark profile={x} size={34} dim={!x.live} />}
                                        name={`Shared from ${line}`}
                                        sub={x.revoked ? `Granted ${dateOf(x.created)} · revoked ${dateOf(x.revoked)}` : x.expired ? `Granted ${dateOf(x.created)} · expired ${dateOf(x.expires)}` : `Granted ${dateOf(x.created)}`}
                                        end={(replacedBy(x, connection) ? 'Not used' : STATUS_WORD[s]).toLowerCase()}
                                        endTone={s === 'active' && !replacedBy(x, connection) ? undefined : 'warn'}
                                        onPress={() => { setPickedId(x.id); setView('rules'); }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="nx-consent__pane">
                        <PreviewPane load={loadPreview} access={p.blob} dead={dead} who={p.label}
                            idle={status === 'paused' ? paused : undefined}
                            empty={inGroup ? `None of ${spaceName}’s nodes: its realms cover none of what was approved.` : 'None of your nodes: its realms cover none of what you approved.'} />
                    </div>
                )}
            </ConsentFrame>


            {reading && <HistoryModal profile={p} schemaNames={data.schemaNames} load={calls.history} onClose={() => setReading(false)} />}
        </Inspector>
    );
}
